import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Wrench, AlertCircle } from 'lucide-react';

interface ModificationCategory {
  id: string;
  name: string;
  description: string;
}

interface ModificationType {
  id: string;
  category_id: string;
  name: string;
  description: string;
}

interface VehicleModificationsProps {
  onSave: (modifications: { type_id: string, details: string }[]) => void;
  onBack: () => void;
}

export function VehicleModifications({ onSave, onBack }: VehicleModificationsProps) {
  const [categories, setCategories] = useState<ModificationCategory[]>([]);
  const [modificationTypes, setModificationTypes] = useState<ModificationType[]>([]);
  const [selectedMods, setSelectedMods] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchModificationData();
  }, []);

  async function fetchModificationData() {
    try {
      setLoading(true);
      setError(null);

      // Fetch categories
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('modification_categories')
        .select('*')
        .order('name');

      if (categoriesError) throw categoriesError;

      // Fetch modification types
      const { data: typesData, error: typesError } = await supabase
        .from('modification_types')
        .select('*')
        .order('name');

      if (typesError) throw typesError;

      setCategories(categoriesData);
      setModificationTypes(typesData);
    } catch (err) {
      setError('Failed to load modification options');
      console.error('Error fetching modification data:', err);
    } finally {
      setLoading(false);
    }
  }

  const handleModificationSelect = (typeId: string, details: string) => {
    if (details.trim() === '') {
      const newMods = { ...selectedMods };
      delete newMods[typeId];
      setSelectedMods(newMods);
    } else {
      setSelectedMods(prev => ({
        ...prev,
        [typeId]: details
      }));
    }
  };

  const handleSubmit = () => {
    const modifications = Object.entries(selectedMods).map(([type_id, details]) => ({
      type_id,
      details
    }));
    onSave(modifications);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2"></div>
          <div className="text-gray-500">Loading modification options...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center">
        <AlertCircle className="w-5 h-5 mr-2" />
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-2 mb-6">
        <Wrench className="w-6 h-6 text-indigo-600" />
        <h2 className="text-xl font-semibold">Vehicle Modifications</h2>
      </div>

      <div className="space-y-8">
        {categories.map(category => {
          const categoryMods = modificationTypes.filter(
            mod => mod.category_id === category.id
          );

          if (categoryMods.length === 0) return null;

          return (
            <div key={category.id} className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">{category.name}</h3>
              <p className="text-sm text-gray-500">{category.description}</p>

              <div className="space-y-4">
                {categoryMods.map(mod => (
                  <div key={mod.id} className="space-y-2">
                    <label className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        checked={mod.id in selectedMods}
                        onChange={(e) => {
                          if (!e.target.checked) {
                            const newMods = { ...selectedMods };
                            delete newMods[mod.id];
                            setSelectedMods(newMods);
                          } else {
                            setSelectedMods(prev => ({
                              ...prev,
                              [mod.id]: ''
                            }));
                          }
                        }}
                        className="mt-1 rounded text-indigo-600 focus:ring-indigo-500"
                      />
                      <div className="flex-1">
                        <span className="block font-medium text-gray-700">{mod.name}</span>
                        <span className="block text-sm text-gray-500">{mod.description}</span>
                      </div>
                    </label>

                    {mod.id in selectedMods && (
                      <div className="ml-8">
                        <textarea
                          value={selectedMods[mod.id]}
                          onChange={(e) => handleModificationSelect(mod.id, e.target.value)}
                          placeholder="Provide details about this modification..."
                          className="w-full p-2 border rounded-md text-sm"
                          rows={2}
                          required
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex justify-between pt-6">
        <button
          type="button"
          onClick={onBack}
          className="px-3 py-1.5 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm"
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          className="px-3 py-1.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm"
        >
          Save Modifications
        </button>
      </div>
    </div>
  );
}